import { Cutlery } from '@creditornot/cb-icons';
import { cbSemanticCssVars, typographyCss } from '@creditornot/cb-ingredients';
import { DateTime } from 'luxon';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { areEqual } from 'react-window';
import styled, { useTheme } from 'styled-components';

import { localize } from 'common/utils';
import SharedMenuStateTag from 'modules/shared-menu/components/SharedMenuStateTag';
import { SelectionInfoContextProvider } from 'modules/shared-menu/contexts/SelectionInfoContext';
import { SharedMenu } from 'modules/shared-menu/types';
import { selectionHasValidLastPublishedValue } from 'modules/shared-menu/utils';

import { SelectionActions } from './SelectionActions';
import { useAutoWidthCell } from './useAutoWidthCell';

type Props = {
  country: Country;
  onEditSelectionVenues: (selection: SharedMenu) => void;
  selection: SharedMenu;
  index: number;
};

const Root = styled.div`
  display: table-row;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  width: 100%;
  vertical-align: middle;
  height: 56px;
`;

const TableCell = styled.td`
  ${typographyCss.Body3}
  vertical-align: middle;
  padding: 0 24px;
`;

const NameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    flex-shrink: 0;
  }
`;

const SelectionListRow = ({ country, onEditSelectionVenues, selection, index }: Props) => {
  const { cbDirection } = useTheme();
  const intl = useIntl();

  const autoWidthCell = useAutoWidthCell(index);

  const hasValidLastPublishedValue = useMemo(() => selectionHasValidLastPublishedValue(selection), [selection]);

  return (
    <SelectionInfoContextProvider
      value={{
        country,
        selection,
      }}
    >
      <Root dir={cbDirection}>
        <TableCell>
          <NameContainer>
            <Cutlery height="16" />
            {localize(selection.name, intl.locale)}
          </NameContainer>
        </TableCell>
        <TableCell>
          <SharedMenuStateTag sharedMenu={selection} />
        </TableCell>
        <TableCell>
          {hasValidLastPublishedValue
            ? DateTime.fromISO(selection.version.updated_at).toLocaleString({
                month: 'short',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })
            : '-'}
        </TableCell>
        <TableCell style={{ width: '150px' }}>{selection.linked_venue_ids.length}</TableCell>
        <TableCell ref={autoWidthCell.targetElementRef}>
          <SelectionActions
            selection={selection}
            ref={autoWidthCell.sourceElementRef}
            onEditSelectionVenues={onEditSelectionVenues}
            venueCount={selection.linked_venue_ids.length}
          />
        </TableCell>
      </Root>
    </SelectionInfoContextProvider>
  );
};

export default React.memo(SelectionListRow, areEqual);
